import { useMemo } from 'react';
import {
  rootId,
  TaskListItemTypes,
  TreeItem,
} from 'shared/components/ui/virtualized-tree-list';
import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';

import { ARCHIVED_SECTION_ID } from '../constants';

export type Options = {
  hideArchived?: boolean;
  hideCompleted?: boolean;
};

export const useNoGroupingGoalListTree = (
  goals: Goal[],
  { hideArchived, hideCompleted }: Options,
) =>
  useMemo<Record<ID, TreeItem>>(() => {
    const activeGoals: ID[] = [];
    const archivedGoals: ID[] = [];

    const goalMap = goals.reduce<Record<ID, Goal>>((acc, goal) => {
      acc[goal.id] = goal;
      return acc;
    }, {});

    const goalChildrenMap = goals.reduce<Record<ID, ID[]>>((acc, goal) => {
      acc[goal.id] = acc[goal.id] ?? [];
      const directParentId = goal.parentIds?.[goal.parentIds.length - 1];
      if (directParentId && goalMap[directParentId]) {
        acc[directParentId] = [...(acc[directParentId] ?? []), goal.id];
      }

      return acc;
    }, {});

    const treeItems: Record<ID, TreeItem> = {};

    goals.forEach((goal) => {
      if (hideArchived && goal.archivedAt) {
        return;
      }
      if (!hideArchived && goal.archivedAt) {
        archivedGoals.push(goal.id);
      }

      if (!hideCompleted || !goal.completedAt) {
        activeGoals.push(goal.id);
      }

      const children =
        goalChildrenMap[goal.id]?.filter((id) => {
          const child = goalMap[id];
          return (
            child && !child.archivedAt && (!hideCompleted || !child.completedAt)
          );
        }) ?? [];
      treeItems[goal.id] = {
        id: goal.id,
        name: goal.name,
        type: TaskListItemTypes.Goal,
        children,
        isFolder: !!children.length,
      };
    });

    if (archivedGoals.length) {
      treeItems[ARCHIVED_SECTION_ID] = {
        id: ARCHIVED_SECTION_ID,
        type: TaskListItemTypes.LifeArea,
        name: 'Archived',
        children: archivedGoals,
        isFolder: true,
      };
    }

    const rootChildren = activeGoals.filter((goalId) => {
      const goal = goalMap[goalId];
      if (goal.archivedAt && !hideArchived) {
        return false;
      }
      // If the goal has parents, but none of them are in the tree, treat it as a root
      return goal.parentIds?.length
        ? !goal.parentIds.some((parentId) => treeItems[parentId])
        : // If the goal has no parents, it is definitely a root
          true;
    }, []);

    if (archivedGoals.length) {
      rootChildren.push(ARCHIVED_SECTION_ID);
    }

    return {
      [rootId]: {
        id: rootId,
        name: 'Root',
        type: TaskListItemTypes.Root,
        children: rootChildren,
      },
      ...treeItems,
    };
  }, [goals, hideArchived, hideCompleted]);
